import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { Cell, UnreadDot, unreadDotCutoutStyle } from '@highlight/ui'

/** Past this the icons crowd out the title, so the rest collapse into a "+N". */
const MAX_VISIBLE = 3

function isUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://')
}

type CellIconProps = ComponentPropsWithoutRef<typeof Cell.Icon>

interface ActionIconSlotProps extends CellIconProps {
  /** The action's view. When it carries several `icons`, they render bare and side by side. */
  view?: { icons?: Array<string> }
  /** The single-icon badge, rendered when the action doesn't span several integrations. */
  children: ReactNode
  /**
   * Absolutely-positioned decoration centred on the tile, behind it in paint
   * order — the executed row's expiry countdown. Ignored in the multi-icon
   * layout, which has no single tile to centre on.
   */
  overlay?: ReactNode
}

/**
 * The badge an action is shown with — a drop-in for `Cell.Icon`.
 *
 * An action spanning several integrations shows its icons bare and side by side:
 * the tile chrome and sub-icon that identify a single integration only add noise
 * once there are several logos to read. The row keeps the height of the tile it
 * replaces so the badge stays aligned with other cells'. Anything else renders
 * exactly as `Cell.Icon` did, so call sites can swap in this component
 * unconditionally.
 */
export function ActionIconSlot({
  view,
  children,
  subIcon,
  overlay,
  unread,
  indicator,
  ...cellIconProps
}: ActionIconSlotProps) {
  const icons = view?.icons?.filter(isUrl) ?? []
  const showIndicator = Boolean(unread || indicator)

  if (icons.length <= 1) {
    const tile = (
      <Cell.Icon subIcon={subIcon} unread={unread} indicator={indicator} {...cellIconProps}>
        {children}
      </Cell.Icon>
    )

    if (!overlay) return tile

    // The overlay renders first so the tile and its sub-icon paint over it —
    // the countdown passes behind the success badge rather than through it.
    return (
      <div className="relative shrink-0 self-start">
        {overlay}
        {tile}
      </div>
    )
  }

  const visible = icons.slice(0, MAX_VISIBLE)
  const overflow = icons.length - visible.length

  return (
    <div className="flex h-5 shrink-0 items-center gap-1 self-start">
      {visible.map((icon, index) => {
        const showDot = showIndicator && index === visible.length - 1
        return (
          <span key={icon} className="relative size-4 shrink-0">
            {/* drop-shadow, not shadow: some toolset logos are bare glyphs on a
                transparent ground (gmail), where a box-shadow would outline an
                invisible rectangle instead of the mark. */}
            <img
              src={icon}
              alt=""
              className="size-4 rounded-md object-contain drop-shadow"
              style={showDot ? unreadDotCutoutStyle : undefined}
            />
            {showDot && <UnreadDot tone={indicator} />}
          </span>
        )
      })}
      {overflow > 0 && <span className="text-foreground-secondary shrink-0 text-[10px] leading-none">+{overflow}</span>}
    </div>
  )
}
